import React from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useMutation } from "@tanstack/react-query";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import {
  addLanguagesToUser,
  deleteUserLanguage,
} from "../../../services/api/language";
import { languageOptions } from "../../../utils/variables";
import { getLanguageLabel } from "../../../utils/helpers";

const proficiencyOptions = [
  { label: "A1", value: "A1" },
  { label: "A2", value: "A2" },
  { label: "B1", value: "B1" },
  { label: "B2", value: "B2" },
  { label: "C1", value: "C1" },
  { label: "C2", value: "C2" },
];

const validationSchema = yup.object({
  languages: yup
    .array() // ✅ Define as an array
    .of(
      yup.object({
        language: yup.string().required("Language is required"),
        proficiency: yup.string().required("Proficiency is required"),
      })
    ) // ✅ Ensure each item is a string
    .min(1, "At least one language is required") // ✅ Ensure at least one selection
    .required("Languages selection is required"), // ✅ Ensure the array itself exists
});

const AddLanguagesModal = ({
  open,
  close,
  refetchAllUserLanguages,
  languagesData,
  targetUserId,
}) => {
  const dispatch = useDispatch();
  const {
    isPending: isAddLanguageLoading,
    error: isAddLanguageError,
    data: addedLanguageData,
    mutateAsync: addLanguagesToUserMutation,
  } = useMutation({
    mutationFn: (payload) => addLanguagesToUser(payload, targetUserId),
    enabled: false,
  });

  const {
    isPending: isDeletedLanguageApiLoading,
    error: isDeletedLanguageApiError,
    data: deletedLanguageData,
    mutateAsync: deleteUserLanguageMutation,
  } = useMutation({
    mutationFn: (id) => deleteUserLanguage(id, targetUserId),
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      languages: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleAddLanguages();
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleDeleteLanguage = async (id) => {
    try {
      await deleteUserLanguageMutation(id);
      refetchAllUserLanguages();
    } catch (error) {}
  };

  const handleAddLanguages = async () => {
    try {
      const userAddedLanguagesResponse = await addLanguagesToUserMutation(
        formik.values
      );
      dispatch(
        showToast({
          message: "Language added successfully",
          type: "success",
        })
      );
      refetchAllUserLanguages();
      close(false);
      formik.resetForm();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };
  const handleAddLanguageProficiency = () => {
    if (formik.values.currentLanguage && formik.values.currentProficiency) {
      formik.setFieldValue("languages", [
        ...formik.values.languages,
        {
          language: formik.values.currentLanguage,
          proficiency: formik.values.currentProficiency,
        },
      ]);
      formik.setFieldValue("currentLanguage", "");
      formik.setFieldValue("currentProficiency", "");
    }
  };
  const handleRemoveLanguage = (lang) => {
    const pending = formik.values.languages || [];
    const hasPending = pending.some((p) => p.language === lang.language);

    if (hasPending) {
      formik.setFieldValue(
        "languages",
        pending.filter((p) => p.language !== lang.language)
      );
    } else if (lang.id) {
      handleDeleteLanguage(lang.id);
    }
  };

  const filteredLanguageOptions = languageOptions?.filter(
    (option) =>
      !languagesData?.data?.some((lang) => lang.language === option.value)
  );

  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={close}
    >
      <div className="pt-7 pb-4 px-12 w-full lg:w-[900px] xl:w-[1000px]">
        <h6 className="text-dark font-normal border-b pb-1">Select Language</h6>

        <div className="flex items-center justify-between mt-4">
          <p className="text-gray-700">
            Your types of work will determine which work orders are available to
            you. Select at least one.
          </p>
          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            disabled={
              !formik.values.currentLanguage || !formik.values.currentProficiency
            }
            onClick={handleAddLanguageProficiency}
          >
            Add
          </BaseButton>
        </div>

        <BaseAutoSelect
          placeholder="Select Language"
          className="mt-6"
          options={filteredLanguageOptions}
          id="language"
          name="language"
          value={formik.values.currentLanguage}
          onChange={(e) =>
            formik.setFieldValue("currentLanguage", e.target.value)
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.languages?.language &&
            Boolean(formik.errors.languages?.language)
          }
          helperText={
            formik.touched.languages?.language &&
            formik.errors.languages?.language
          }
        />
        <BaseAutoSelect
          placeholder="Select Language Proficiency"
          className="mt-4"
          options={proficiencyOptions}
          id="proficiency"
          name="proficiency"
          value={formik.values.currentProficiency}
          onChange={(e) =>
            formik.setFieldValue("currentProficiency", e.target.value)
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.languages?.proficiency &&
            Boolean(formik.errors.languages?.proficiency)
          }
          helperText={
            formik.touched.languages?.proficiency &&
            formik.errors.languages?.proficiency
          }
        />

        <div className="flex flex-wrap gap-3 mt-4">
          {[
            ...(languagesData?.data || []),
            ...(formik.values.languages || []),
          ]?.map((lang, idx) => (
            <div
              key={lang.id ?? `${lang.language}-${idx}`}
              className="flex items-center px-4 py-1 bg-light rounded-sm"
            >
              <p>
                {getLanguageLabel(lang.language)} — {lang.proficiency}
              </p>
              <i
                className="fa-solid fa-xmark ml-2 cursor-pointer hover:text-blue-500"
                onClick={() => handleRemoveLanguage(lang)}
              />
            </div>
          ))}
        </div>
      </div>
      <div
        className="d-flex content-center bg-white py-6 mt-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px]"
          color="primary"
          onClick={formik.handleSubmit}
          // Disabled by default; enabled only when there are unsaved changes.
          disabled={!formik.dirty || !formik.values.languages?.length || isAddLanguageLoading}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default AddLanguagesModal;
